import { useEffect, useState } from "react";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { academyRequest, track } from "@/lib/academy/actions";
import { safeStorage } from "@/lib/safeStorage";

const JOINED_KEY = "dcyb_academy_waitlist";
const JOINED_EVENT = "dcyb:academy-waitlist-joined";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const DONE = { success: "You're on the list.", joined: "You're on the list." };

const WaitlistDone = ({ status, testId, dark, onReset }) => (
  <div data-testid={`${testId}-status-message`} role="status" className={`flex items-start gap-3 border p-4 sm:p-5 ${dark ? "border-white/25 bg-white/5 text-white" : "border-[#160907] bg-[#FFF6F1] text-[#160907]"}`}>
    <CheckCircle2 size={20} aria-hidden="true" className="shrink-0 mt-0.5 text-[#F6101A] teaser-check" />
    <div className="min-w-0">
      <p className="font-display text-base sm:text-lg font-bold">{DONE[status]}</p>
      <p className={`mt-1 text-sm leading-relaxed ${dark ? "text-[#E9DCD8]" : "text-[#3D4049]"}`}>We'll send one email the moment DCYB Academy opens. No spam.</p>
      <button type="button" data-testid={`${testId}-add-another`} onClick={onReset} className={`mt-3 text-xs font-semibold underline underline-offset-4 decoration-[#F6101A] ${dark ? "text-white" : "text-[#160907]"}`}>Add another email</button>
    </div>
  </div>
);

export const WaitlistForm = ({ source, testId, dark = false, className = "" }) => {
  const [email, setEmail] = useState("");
  const [trap, setTrap] = useState("");
  const [status, setStatus] = useState(() => (safeStorage.get(JOINED_KEY) ? "joined" : "idle"));
  const [error, setError] = useState("");

  useEffect(() => {
    const sync = () => setStatus((current) => (current === "idle" ? "joined" : current));
    window.addEventListener(JOINED_EVENT, sync);
    return () => window.removeEventListener(JOINED_EVENT, sync);
  }, []);

  const submit = async (event) => {
    event.preventDefault();
    const value = email.trim();
    if (!EMAIL_RE.test(value)) { setError("Enter a valid email address."); return; }
    setStatus("loading");
    setError("");
    try {
      await academyRequest("/waitlist", { method: "POST", body: JSON.stringify({ email: value, source, website: trap }) });
      safeStorage.set(JOINED_KEY, "1");
      setStatus("success");
      track("academy_waitlist_joined", { source });
      window.dispatchEvent(new Event(JOINED_EVENT));
    } catch (err) {
      setStatus("idle");
      setError(err.message || "We couldn't add you just now. Please try again.");
    }
  };

  const reset = () => { safeStorage.remove(JOINED_KEY); setEmail(""); setStatus("idle"); };
  if (DONE[status]) return <div className={className}><WaitlistDone status={status} testId={testId} dark={dark} onReset={reset} /></div>;

  const loading = status === "loading";
  const errorId = `${testId}-error`;
  return (
    <form data-testid={`${testId}-form`} onSubmit={submit} noValidate className={`ph-no-capture ${className}`}>
      <label htmlFor={`${testId}-email`} className={`block mb-2 font-space-mono text-[10px] ${dark ? "text-[#E9DCD8]" : "text-[#3C2A27]"}`}>Get notified at launch</label>
      <div className={`teaser-field flex flex-col sm:flex-row gap-1.5 p-1.5 ${dark ? "teaser-field-dark" : ""}`}>
        <input id={`${testId}-email`} data-testid={`${testId}-email-input`} type="email" inputMode="email" autoComplete="email" required placeholder="you@email.com" value={email} disabled={loading} aria-invalid={Boolean(error)} aria-describedby={error ? errorId : undefined} onChange={(event) => { setEmail(event.target.value); if (error) setError(""); }} className="h-12 shrink-0 sm:shrink sm:flex-1 min-w-0 bg-transparent px-3 text-base text-[#160907] placeholder:text-[#8A8D93] outline-none" />
        <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" value={trap} onChange={(event) => setTrap(event.target.value)} className="absolute w-px h-px opacity-0 pointer-events-none -left-[9999px]" />
        <button type="submit" data-testid={`${testId}-submit-button`} disabled={loading} className={`academy-btn ${dark ? "teaser-btn-red" : "academy-btn-primary"} h-12 !py-0 justify-center shrink-0`}>
          {loading ? <><Loader2 size={15} aria-hidden="true" className="animate-spin" />Adding you…</> : <>Notify Me at Launch<ArrowRight size={15} aria-hidden="true" /></>}
        </button>
      </div>
      <p aria-live="polite" id={errorId} data-testid={`${testId}-error`} className={`min-h-[1.25rem] mt-2 text-sm ${dark ? "text-[#FF9A9E]" : "text-[#D00508]"}`}>{error}</p>
      <p className={`text-xs ${dark ? "text-[#C9B8B4]" : "text-[#5F6168]"}`}>One email when it opens. No spam, and we never share your address.</p>
    </form>
  );
};
